'use client';

/**
 * ToolTimeoutMessage — "leave a message" form of the tool-timeout recovery
 * flow (lib/tool-timeout-view.ts's ToolTimeoutView.MESSAGE). Unlike
 * handoff-timeout-message.tsx (which only needs a textarea — the live
 * handoff already has name/email from the pre-handoff ContactInfoCard),
 * this flow can be reached with NO contact info on file yet, so it captures
 * Name + Email itself alongside the message.
 *
 * Owns its own field state locally (same rationale as every sibling form in
 * this tree — handoff-timeout-message.tsx, handoff-timeout-callback.tsx,
 * contact-info-card.tsx): a failed submit leaves this component mounted so
 * the user's typed draft survives a retry for free.
 *
 * Reuses lib/contact-form-validation.ts for name/email shape checking
 * rather than reinventing it — see that module's own doc comment. `noValidate`
 * on the <form> is required: without it, the browser's native type="email"
 * constraint silently cancels submit before our styled error ever renders
 * (see contact-info-card.tsx:210's identical note — same underlying bug).
 *
 * Submission is fully delegated via `onSubmit` — this component never calls
 * the API itself (that's the container's job, per this task's data-layer
 * boundary).
 *
 * Field ids are prefixed with React 19's `useId()`, not hardcoded strings —
 * this leaf can now be legitimately mounted TWICE at once (the transcript's
 * ToolTimeoutCard and the call surface's CallToolTimeoutPanel both render
 * off the SAME shared tool-timeout flow — see useToolTimeoutFlow's header
 * comment), and duplicate DOM ids are both invalid HTML and a real
 * accessibility bug: a `label`/`for` or `aria-describedby` reference
 * resolves against whichever element the document-wide id index finds
 * first, so a label tap on one surface could focus the field on the OTHER
 * surface, and a screen reader could announce the wrong validation error.
 */
import { useId, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';
import {
  validateContactForm,
  type ContactFormValues,
} from '@/lib/contact-form-validation';
import { StandardCard } from './primitives/standard-card';

const FIELDS = [
  { name: 'name' as const, required: true },
  { name: 'email' as const, required: true },
];

interface ToolTimeoutMessageProps {
  onBack: () => void;
  onSubmit: (values: { name: string; email: string; body: string }) => void;
  submitting?: boolean;
  error?: string | null;
  /** Forwarded straight to the underlying StandardCard — see
   *  tool-timeout-choose.tsx's identical prop for the full "why" (the
   *  voice-call surface's CallToolTimeoutPanel widening this past
   *  StandardCard's default transcript-lane cap). Omitted by text chat. */
  className?: string;
}

export function ToolTimeoutMessage({
  onBack,
  onSubmit,
  submitting = false,
  error,
  className,
}: ToolTimeoutMessageProps) {
  const [values, setValues] = useState<ContactFormValues>({
    name: '',
    email: '',
    phone: '',
  });
  const [body, setBody] = useState('');
  const [touched, setTouched] = useState<Partial<Record<string, boolean>>>({});
  // One stable id per MOUNTED instance (see file header) — this form can be
  // simultaneously on-screen twice (text chat + the call surface, driven by
  // the same shared flow), so hardcoded ids would collide across them.
  const uid = useId();
  const bodyId = `${uid}-body`;

  const errors = validateContactForm(FIELDS, values);
  const canSubmit =
    Object.keys(errors).length === 0 && body.trim().length > 0 && !submitting;

  const handleBlur = (name: string) =>
    setTouched((prev) => ({ ...prev, [name]: true }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true });
    if (Object.keys(errors).length > 0 || body.trim().length === 0) return;
    onSubmit({
      name: values.name.trim(),
      email: values.email.trim(),
      body: body.trim(),
    });
  };

  return (
    <StandardCard
      eyebrow="LEAVE A MESSAGE"
      title="What were you looking for?"
      className={className}
    >
      <div className="flex flex-col gap-[14px]">
        <div className="text-[13.5px]" style={{ color: V3_COLORS.muted }}>
          A live agent will get back to you — usually within a few hours.
          I&apos;ll attach what you asked me to find.
        </div>

        <form
          onSubmit={handleSubmit}
          // See this file's header comment — without noValidate, an invalid
          // type="email" value silently cancels submit before our own
          // styled error can render.
          noValidate
          className="flex flex-col gap-[14px]"
        >
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            {(['name', 'email'] as const).map((name) => {
              const inputId = `${uid}-${name}`;
              const errorId = `${inputId}-error`;
              const hasError = touched[name] && !!errors[name];
              return (
                <div key={name} className="flex flex-col gap-1">
                  <label
                    htmlFor={inputId}
                    className="text-[12.5px] font-semibold"
                    style={{ color: V3_COLORS.muted }}
                  >
                    {name === 'name' ? 'Name' : 'Email'}
                  </label>
                  <input
                    id={inputId}
                    type={name === 'email' ? 'email' : 'text'}
                    autoComplete={name}
                    value={values[name]}
                    onChange={(e) =>
                      setValues((prev) => ({ ...prev, [name]: e.target.value }))
                    }
                    onBlur={() => handleBlur(name)}
                    disabled={submitting}
                    aria-invalid={hasError}
                    aria-describedby={hasError ? errorId : undefined}
                    className={cn(
                      'rounded-[11px] border-[1px] bg-[#FEFCF9] px-3.5 py-[11px]',
                      'text-[14.5px]',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e36001]/25',
                      hasError
                        ? 'border-[#b3261e]'
                        : 'border-[#e6dccd] focus-visible:border-[#e36001]/60',
                      'disabled:opacity-50'
                    )}
                    style={{ color: V3_COLORS.ink }}
                  />
                  {hasError && (
                    <span
                      id={errorId}
                      role="alert"
                      className="text-[12px]"
                      style={{ color: '#b3261e' }}
                    >
                      {errors[name]}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor={bodyId}
              className="text-[12.5px] font-semibold"
              style={{ color: V3_COLORS.muted }}
            >
              What are you looking for?
            </label>
            <textarea
              id={bodyId}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={submitting}
              placeholder="Type your question or what you'd like help with…"
              rows={3}
              className={cn(
                'min-h-[84px] rounded-[12px] border-[1px] border-[#e6dccd] bg-[#FEFCF9] px-[15px] py-[13px]',
                'text-[14px] leading-[1.55]',
                'placeholder:text-[#b3a996]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e36001]/25',
                'disabled:opacity-50'
              )}
              style={{ color: V3_COLORS.ink }}
            />
          </div>

          {error && (
            <div
              role="alert"
              className="text-[12.5px]"
              style={{ color: '#b3261e' }}
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              className="flex min-h-[44px] items-center gap-1 text-[14px] font-semibold disabled:opacity-50"
              style={{ color: V3_COLORS.muted }}
            >
              <ChevronLeft aria-hidden="true" className="size-[14px]" />
              Back
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={cn(
                'min-h-[44px] rounded-[22px] px-[18px] py-[10px] text-[13.5px] font-semibold text-white',
                'shadow-[0_10px_22px_-12px_rgba(227,96,1,0.7)]',
                canSubmit
                  ? 'bg-[#E36001]'
                  : 'cursor-not-allowed bg-[#E36001]/60'
              )}
            >
              {submitting ? 'Sending…' : 'Send message'}
            </button>
          </div>
        </form>
      </div>
    </StandardCard>
  );
}
